import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Row, Col } from 'react-bootstrap';
import Icon from '@mdi/react';
import { mdiTshirtCrew } from '@mdi/js';
import axios from 'axios';

function AddTeamToSeasonModal({ show, handleClose, team, onTeamUpdated }) {
    const [formData, setFormData] = useState({
        season: '',
        captain: '',
        a_cap: '',
        color: '',
    });
    const [seasons, setSeasons] = useState([]);
    const [players, setPlayers] = useState([]);
    const [colorChoices, setColorChoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [playersLoading, setPlayersLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!show || !team?.league) {
            return;
        }

        let cancelled = false;

        const loadFormData = async () => {
            try {
                setDataLoading(true);
                setError(null);
                const [seasonsResponse, colorsResponse] = await Promise.all([
                    axios.get(`/api/leagues/seasons/?league=${team.league}`, {
                        withCredentials: true,
                    }),
                    axios.get('/api/teams/team-color-choices/', {
                        withCredentials: true,
                    }),
                ]);

                if (cancelled) {
                    return;
                }

                const availableSeasons = (seasonsResponse.data || []).filter(
                    (season) => season.id !== team.current_season
                );

                setSeasons(availableSeasons);
                setPlayers([]);
                setColorChoices(colorsResponse.data.choices || []);
                setFormData({
                    season: availableSeasons[0]?.id?.toString() || '',
                    captain: '',
                    a_cap: '',
                    color: team.color || '',
                });
            } catch (loadError) {
                if (cancelled) {
                    return;
                }
                console.error('Error loading add-to-season modal:', loadError);
                setError('Failed to load form details.');
            } finally {
                if (!cancelled) {
                    setDataLoading(false);
                }
            }
        };

        loadFormData();

        return () => {
            cancelled = true;
        };
    }, [show, team]);

    useEffect(() => {
        if (!show || !team?.id || !formData.season) {
            setPlayers([]);
            return;
        }

        let cancelled = false;

        const loadEligiblePlayers = async () => {
            try {
                setPlayersLoading(true);
                setError(null);
                const response = await axios.get(`/api/teams/${team.id}/eligible_season_players/`, {
                    params: { season_id: formData.season },
                    withCredentials: true,
                });

                if (cancelled) {
                    return;
                }

                const eligiblePlayers = response.data || [];

                setPlayers(eligiblePlayers);
                setFormData((prev) => ({
                    ...prev,
                    captain: eligiblePlayers.some((player) => player.id.toString() === prev.captain)
                        ? prev.captain
                        : '',
                    a_cap: eligiblePlayers.some((player) => player.id.toString() === prev.a_cap)
                        ? prev.a_cap
                        : '',
                }));
            } catch (loadError) {
                if (cancelled) {
                    return;
                }
                console.error('Error loading eligible players:', loadError);
                setError('Failed to load eligible captains for the selected season.');
                setPlayers([]);
            } finally {
                if (!cancelled) {
                    setPlayersLoading(false);
                }
            }
        };

        loadEligiblePlayers();

        return () => {
            cancelled = true;
        };
    }, [formData.season, show, team]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                season: formData.season,
                captain: formData.captain,
                a_cap: formData.a_cap || '',
                color: formData.color,
            };

            const response = await axios.post(
                `/api/teams/${team.id}/add_to_season/`,
                payload,
                {
                    withCredentials: true,
                }
            );

            onTeamUpdated(response.data);
        } catch (submitError) {
            const responseData = submitError.response?.data;
            if (typeof responseData === 'object' && responseData !== null) {
                const message = Object.entries(responseData)
                    .flatMap(([field, value]) => (Array.isArray(value) ? value : [value]).map((item) => `${field}: ${item}`))
                    .join(' ');
                setError(message || 'Failed to add team to season.');
            } else {
                setError('Failed to add team to season.');
            }
        } finally {
            setLoading(false);
        }
    };

    const seasonDisabled = seasons.length === 0;

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Add Team to Season</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    {dataLoading ? (
                        <div className="text-center py-4">Loading form details...</div>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col>
                                    <Form.Group controlId="season">
                                        <Form.Label>Season</Form.Label>
                                        <Form.Select
                                            name="season"
                                            value={formData.season}
                                            onChange={handleChange}
                                            required
                                            disabled={seasonDisabled}
                                        >
                                            <option value="">Select a season</option>
                                            {seasons.map((season) => (
                                                <option key={season.id} value={season.id}>
                                                    {season.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        {seasonDisabled && (
                                            <Form.Text className="text-muted">
                                                No additional seasons are currently available for this team.
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group controlId="captain">
                                        <Form.Label>Captain</Form.Label>
                                        <Form.Select
                                            name="captain"
                                            value={formData.captain}
                                            onChange={handleChange}
                                            required
                                            disabled={playersLoading || players.length === 0}
                                        >
                                            <option value="">Select a captain</option>
                                            {players.map((player) => (
                                                <option key={`captain-${player.id}`} value={player.id}>
                                                    {player.first_name} {player.last_name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="a_cap">
                                        <Form.Label>Assistant Captain</Form.Label>
                                        <Form.Select
                                            name="a_cap"
                                            value={formData.a_cap}
                                            onChange={handleChange}
                                            disabled={playersLoading || players.length === 0}
                                        >
                                            <option value="">No assistant captain</option>
                                            {players.map((player) => (
                                                <option key={`a_cap-${player.id}`} value={player.id}>
                                                    {player.first_name} {player.last_name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col>
                                    {playersLoading ? (
                                        <Form.Text className="text-muted">
                                            Loading eligible players...
                                        </Form.Text>
                                    ) : (
                                        <Form.Text className="text-muted">
                                            Captain options include only players who were on this team previously,
                                            are registered for the selected season, and are not currently assigned to a team.
                                        </Form.Text>
                                    )}
                                </Col>
                            </Row>

                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group controlId="color">
                                        <Form.Label>Team Color</Form.Label>
                                        <Form.Select
                                            name="color"
                                            value={formData.color}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select a color</option>
                                            {colorChoices.map(([value, label]) => (
                                                <option key={value} value={value}>
                                                    {label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <div className="text-center mt-2">
                                            <Icon
                                                path={mdiTshirtCrew}
                                                size={11}
                                                style={{ color: formData.color || 'black' }}
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={loading || dataLoading || seasonDisabled}
                    >
                        {loading ? 'Saving...' : 'Add to Season'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default AddTeamToSeasonModal;
